import React, {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import {useNavigate} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {
  formatSiteSeconds,
  getTimeOnSiteCompanies,
  getTimeOnSiteReport,
  statusWords,
  stayLine,
  whenLine,
} from './time-on-site.service';
import {TimeOnSiteCompanyDto, TimeOnSiteReportDto, TimeOnSiteRowDto} from './dtos/admin-time-on-site.dto';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';

/** A person's stays listed under their name; more are counted, not listed. */
const STAYS_LISTED = 3;

const SiteTable = ({label, kind, rows}: {label: string; kind: 'appointment' | 'job'; rows: TimeOnSiteRowDto[]}) => (
  <Box sx={{mb: 4}}>
    <Typography variant="h6" component="h2" sx={{mb: 1}}>{label}</Typography>
    {rows.length === 0 ? (
      <Typography color="text.secondary">
        {kind === 'appointment' ? 'Nobody has been at an appointment.' : 'Nobody has been at a job.'}
      </Typography>
    ) : (
      <TableContainer>
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              <TableCell>Site</TableCell>
              <TableCell>{kind === 'appointment' ? 'Appointment' : 'Job'}</TableCell>
              <TableCell align="right">Time on site</TableCell>
              <TableCell>People</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const when = whenLine(row.appointmentTime, row.scheduledDate);
              return (
                <TableRow key={`${row.id ?? 'none'}-${row.estimateId}`} hover sx={{verticalAlign: 'top'}}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.clientName ?? `Estimate ${row.estimateId}`}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.address || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.id == null ? 'No appointment' : when ?? (kind === 'job' ? 'No date' : 'No time set')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[row.id == null ? `Estimate ${row.estimateId}` : `#${row.id}`, statusWords(row.status)].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} noWrap>{formatSiteSeconds(row.totalSeconds)}</Typography>
                    {row.onSiteNow && <Chip size="small" color="success" label="On site now" sx={{mt: 0.5}}/>}
                  </TableCell>
                  <TableCell>
                    {row.people.map(person => (
                      <Box key={person.userId} sx={{mb: 1}}>
                        <Typography variant="body2">
                          {person.name ?? 'Former team member'}
                          <Typography component="span" variant="body2" fontWeight={600}> · {formatSiteSeconds(person.seconds)}</Typography>
                        </Typography>
                        {person.stays.slice(0, STAYS_LISTED).map(stay => (
                          <Typography key={stay.arrivedAt} variant="caption" color="text.secondary" display="block">
                            {stayLine(stay)}
                          </Typography>
                        ))}
                        {person.stays.length > STAYS_LISTED && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            and {person.stays.length - STAYS_LISTED} more
                          </Typography>
                        )}
                      </Box>
                    ))}
                    {row.people.length === 0 && (
                      <Typography variant="caption" color="text.secondary">Just arrived; counted from two minutes on.</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

// Time on site (V84), for SavaQuan staff: what the crews' and estimators'
// phones recorded at each appointment's and job's address, for one company.
// The phones' geofences report arrivals and departures and nothing else of
// where anyone is; no company page or app shows these figures. A visit
// belongs to the job from the moment the estimate was approved into it,
// before that to the appointment (SiteVisitReport); stays under two
// minutes are drive-bys and do not count (SiteTime).
const TimeOnSiteTab: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<TimeOnSiteCompanyDto[] | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [report, setReport] = useState<TimeOnSiteReportDto | null>(null);
  const [loading, setLoading] = useState(true);

  const failed = useCallback((context: string, e: unknown) => {
    if (isAuthRedirectError(e)) return;
    logError(context, {}, e as Error);
    enqueueSnackbar((e as Error).message, {variant: 'error', autoHideDuration: 8000});
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getTimeOnSiteCompanies(navigate);
      setCompanies(list);
      // The most recent company first, unless one was already picked.
      setCompanyId(current => current != null && list.some(c => c.companyId === current) ? current : list[0]?.companyId ?? null);
    } catch (e) {
      failed('Time on site companies failed', e);
    } finally {
      setLoading(false);
    }
  }, [navigate, failed]);

  const loadReport = useCallback(async (id: number) => {
    setLoading(true);
    try {
      setReport(await getTimeOnSiteReport(navigate, id));
    } catch (e) {
      failed('Time on site report failed', e);
    } finally {
      setLoading(false);
    }
  }, [navigate, failed]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (companyId != null) void loadReport(companyId);
  }, [companyId, loadReport]);

  const refresh = () => {
    void loadCompanies();
    if (companyId != null) void loadReport(companyId);
  };

  const shown = report != null && report.companyId === companyId ? report : null;

  return (
    <Box sx={{p: 3}}>
      <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 3}}>
        <TextField
          select
          label="Company"
          value={companyId ?? ''}
          onChange={event => setCompanyId(Number(event.target.value))}
          size="small"
          sx={{flex: 1, maxWidth: 420}}
          disabled={!companies || companies.length === 0}
        >
          {(companies ?? []).map(company => (
            <MenuItem key={company.companyId} value={company.companyId}>
              {company.name} ({company.visits} {company.visits === 1 ? 'visit' : 'visits'})
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={refresh} disabled={loading} startIcon={<SyncIcon/>}>
          Refresh
        </Button>
      </Box>

      {loading && !shown && <CircularProgress sx={{m: 3}}/>}

      {companies && companies.length === 0 && (
        <Typography color="text.secondary">No phone has recorded a visit yet.</Typography>
      )}

      {shown && (
        <>
          <SiteTable label="By appointment" kind="appointment" rows={shown.appointments}/>
          <SiteTable label="By job" kind="job" rows={shown.jobs}/>
        </>
      )}
    </Box>
  );
};

export default TimeOnSiteTab;
